import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LearningProgress = () => {
  const currentLearning = [
    {
      id: 1,
      name: "Rust Programming",
      category: "Systems Programming",
      progress: 65,
      goal: "Build high-performance web services",
      timeline: "3 months",
      resources: [
        { name: "The Rust Programming Language", type: "Book", status: "In Progress" },
        { name: "Rustlings Exercises", type: "Practice", status: "Completed" },
        { name: "Building Web APIs with Rust", type: "Course", status: "Planned" }
      ],
      color: "bg-orange-600",
      icon: "Zap"
    },
    {
      id: 2,
      name: "WebAssembly",
      category: "Web Performance",
      progress: 40,
      goal: "Optimize compute-heavy frontend operations",
      timeline: "2 months",
      resources: [
        { name: "WebAssembly Fundamentals", type: "Course", status: "In Progress" },
        { name: "WASM by Example", type: "Tutorial", status: "In Progress" },
        { name: "Rust and WebAssembly", type: "Book", status: "Planned" }
      ],
      color: "bg-purple-600",
      icon: "Cpu"
    },
    {
      id: 3,
      name: "Three.js & WebGL",
      category: "3D Graphics",
      progress: 25,
      goal: "Create immersive 3D web experiences",
      timeline: "4 months",
      resources: [
        { name: "Three.js Journey", type: "Course", status: "In Progress" },
        { name: "WebGL Fundamentals", type: "Tutorial", status: "Planned" },
        { name: "3D Graphics Projects", type: "Practice", status: "Planned" }
      ],
      color: "bg-blue-600",
      icon: "Box"
    }
  ];

  const upcomingGoals = [
    {
      name: "Blockchain Development",
      description: "Smart contracts and DeFi applications",
      priority: "High",
      timeline: "Q2 2024"
    },
    {
      name: "Mobile Development",
      description: "Flutter for cross-platform apps",
      priority: "Medium",
      timeline: "Q3 2024"
    },
    {
      name: "Game Development",
      description: "Unity and C# for indie games",
      priority: "Low",
      timeline: "Q4 2024"
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="space-y-8">
      {/* Currently Learning */}
      <div className="bg-white rounded-2xl p-8 shadow-soft border border-border">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-text-primary mb-2">Currently Learning</h2>
          <p className="text-text-secondary">
            Active learning projects and skill development in progress.
          </p>
        </div>

        <div className="grid gap-6">
          {currentLearning?.map((item) => (
            <div key={item?.id} className="bg-muted rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-lg ${item?.color} flex items-center justify-center`}>
                    <Icon name={item?.icon} size={24} color="white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">{item?.name}</h3>
                    <p className="text-text-secondary text-sm">{item?.category}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-accent">{item?.progress}%</div>
                  <div className="text-sm text-text-secondary">Complete</div>
                </div>
              </div>

              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-500 ${item?.color}`}
                    style={{ width: `${item?.progress}%` }}
                  ></div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-sm font-medium text-text-primary mb-1">Learning Goal</div>
                  <div className="text-sm text-text-secondary">{item?.goal}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-text-primary mb-1">Timeline</div>
                  <div className="text-sm text-text-secondary">{item?.timeline}</div>
                </div>
              </div>

              <div>
                <div className="text-sm font-medium text-text-primary mb-2">Learning Resources</div>
                <div className="space-y-2">
                  {item?.resources?.map((resource, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={resource?.type === 'Book' ? 'BookOpen' : resource?.type === 'Course' ? 'Play' : 'FileText'} 
                          size={14} 
                          className="text-text-secondary" 
                        />
                        <span className="text-sm text-text-primary">{resource?.name}</span>
                        <span className="text-xs text-text-secondary">({resource?.type})</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        resource?.status === 'Completed' ? 'bg-success text-white' :
                        resource?.status === 'In Progress'? 'bg-accent text-white' : 'bg-gray-200 text-text-secondary'
                      }`}>
                        {resource?.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Upcoming Learning Goals */}
      <div className="bg-white rounded-2xl p-8 shadow-soft border border-border">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-text-primary mb-2">Upcoming Learning Goals</h2>
          <p className="text-text-secondary">
            Planned skill development and technology exploration for the coming months.
          </p>
        </div>

        <div className="space-y-4">
          {upcomingGoals?.map((goal, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-xl">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                  <Icon name="Target" size={20} className="text-text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">{goal?.name}</h3>
                  <p className="text-sm text-text-secondary">{goal?.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-xs px-3 py-1 rounded-full border ${getPriorityColor(goal?.priority)}`}>
                  {goal?.priority} Priority
                </span>
                <span className="text-sm text-text-secondary">{goal?.timeline}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-text-primary">Want to suggest a learning path?</h3>
              <p className="text-sm text-text-secondary">I'm always open to exploring new technologies and skills.</p>
            </div>
            <Button
              variant="outline"
              iconName="MessageCircle"
              iconPosition="left"
              className="text-accent border-accent hover:bg-accent hover:text-white"
            >
              Share Ideas
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningProgress;